import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  trackLandingView,
  trackLandingCTAClick,
  trackLandingScrollDepth,
  trackRegisterButtonClick,
} from "@/utils/analytics";
import {
  Sparkles, ArrowRight, Trophy, Mail, Send,
} from "lucide-react";

// Footer is rendered globally in App.js — do not render it here.


// Redesigned landing components
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import LandingNav from "@/components/landing/LandingNav";
import HeroSection from "@/components/landing/HeroSection";
import WhatIsThisSection from "@/components/landing/WhatIsThisSection";
import MeetLemonBotSection from "@/components/landing/MeetLemonBotSection";
import PlatformPreviewSection from "@/components/landing/PlatformPreviewSection";
import LearningPathsSection from "@/components/landing/LearningPathsSection";
import EducatorsSection from "@/components/landing/EducatorsSection";
import ImpactSection from "@/components/landing/ImpactSection";
import PartnerSection from "@/components/landing/PartnerSection";
import FinalCtaSection from "@/components/landing/FinalCtaSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LaunchLandingPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Contact form state preserved from prior implementation.
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    type: "educator",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isPilotInquiry, setIsPilotInquiry] = useState(false);

  // ---- Analytics: landing view + scroll depth -------------------------
  useEffect(() => {
    trackLandingView({ page: "launch" });
    if (typeof window.fbq === "function") {
      window.fbq("track", "ViewContent", {
        content_name: "Launch Landing Page",
        content_category: "Landing Page",
      });
    }
    const milestones = [25, 50, 75, 100];
    const hit = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = (doc.scrollTop + window.innerHeight) / doc.scrollHeight;
      const pct = Math.round(scrolled * 100);
      milestones.forEach((m) => {
        if (pct >= m && !hit.has(m)) {
          hit.add(m);
          trackLandingScrollDepth({ page: "launch", depth: m });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ---- ?pilot=elementary deep-link prefill ----------------------------
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("pilot") === "elementary") {
      setIsPilotInquiry(true);
      setContactForm((prev) => ({
        ...prev,
        type: "educator",
        message:
          prev.message ||
          "Interested in Lemonade Leaders Jr. pilot. Please share next steps.",
      }));
      // Smooth-scroll to the contact form once mounted
      setTimeout(() => {
        const el = document.getElementById("contact");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  }, [location.search]);

  // ---- CTA helpers ----------------------------------------------------
  const goRegister = (role, ctaId, ctaLabel) => {
    trackLandingCTAClick({
      page: "launch",
      ctaId,
      ctaLabel,
      destination: role ? `/register?role=${role}` : "/register",
    });
    if (role) {
      trackRegisterButtonClick({ page: "launch", role });
      navigate(`/register?role=${role}`);
    } else {
      navigate("/register");
    }
  };

  const scrollTo = (id, ctaId, ctaLabel) => {
    trackLandingCTAClick({
      page: "launch",
      ctaId,
      ctaLabel,
      destination: `#${id}`,
    });
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ---- Hero handlers --------------------------------------------------
  const handleHeroPrimary = (audience) => {
    const role = audience === "educator" ? "educator" : "student";
    goRegister(role, `hero-start-${audience}`, `Start Learning (${audience})`);
  };
  const handleViewDemo = () =>
    scrollTo("platform", "hero-view-demo", "View Demo");
  const handleBookDemo = () =>
    scrollTo("contact", "hero-book-demo", "Book A School Demo");

  // ---- Path card click ------------------------------------------------
  const handleSelectPath = (path) => {
    trackLandingCTAClick({
      page: "launch",
      ctaId: `path-${path.key}`,
      ctaLabel: path.title,
      destination: path.ctaHref,
    });
    if (path.ctaHref.startsWith("/")) navigate(path.ctaHref);
  };

  // ---- Contact form ---------------------------------------------------
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isPilotInquiry) {
        await axios.post(`${API}/leads/elementary-pilot`, {
          name: contactForm.name,
          email: contactForm.email,
          organization:
            contactForm.type === "educator"
              ? "School / Educator"
              : contactForm.type,
          message: contactForm.message,
        });
      } else {
        await new Promise((resolve) => setTimeout(resolve, 600));
      }
      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead", {
          content_name: isPilotInquiry ? "elementary_pilot" : contactForm.type,
          content_category: "Contact Form",
        });
      }
      setSubmitted(true);
    } catch (err) {
      console.error("[contact] submit failed:", err);
      alert(
        "Sorry — we couldn't submit your message just now. Please try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-black antialiased">
      <AnnouncementBar />
      <LandingNav
        onGetStarted={() => goRegister("student", "nav-get-started", "Get Started (nav)")}
      />

      <main>
        <HeroSection
          onPrimary={handleHeroPrimary}
          onDemo={handleViewDemo}
          onBookDemo={handleBookDemo}
        />

        <WhatIsThisSection />

        <MeetLemonBotSection
          onLearnMore={() =>
            scrollTo("platform", "lemonbot-learn-more", "See LemonBot in Action")
          }
        />

        <PlatformPreviewSection />

        <LearningPathsSection onSelectPath={handleSelectPath} />

        <EducatorsSection onBookDemo={handleBookDemo} />

        {/* Responsible AI Statement — preserved from compliance sprint */}
        <section
          id="responsible-ai"
          className="bg-gradient-to-br from-sky-50 via-white to-emerald-50 py-20 sm:py-24"
          data-testid="landing-responsible-ai"
        >
          <div className="max-w-5xl mx-auto px-4 lg:px-6 grid lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-800 px-3 py-1.5 rounded-full text-[11px] font-black tracking-widest uppercase mb-3">
                <Sparkles className="w-3.5 h-3.5" /> Responsible AI
              </div>
              <h2
                className="text-3xl sm:text-4xl font-black tracking-tighter leading-[1.05] text-black"
                data-testid="landing-responsible-ai-heading"
              >
                AI assists learning — it doesn't replace student thinking.
              </h2>
              <p className="mt-4 text-base text-gray-700 leading-relaxed">
                LemonBot is our AI coach. It asks questions, reflects what
                students share, and points them toward their own ideas. It
                never writes the business plan for them.
              </p>
              <button
                onClick={() =>
                  navigate("/security-and-student-data#responsible-ai")
                }
                className="mt-4 inline-flex items-center gap-2 text-sky-700 hover:text-sky-900 font-bold text-sm"
                data-testid="landing-responsible-ai-cta"
              >
                Read our full Responsible AI Statement{" "}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <ul
              className="lg:col-span-3 grid sm:grid-cols-2 gap-3"
              data-testid="landing-responsible-ai-promises"
            >
              {[
                { h: "Coach, not ghostwriter", p: "Socratic prompts only. The student's voice stays the student's voice." },
                { h: "Human creativity at the center", p: "Every Mini Business Plan, every pitch, belongs to the student." },
                { h: "Safety & privacy first", p: "Chats don't train any AI model. PII never leaves our control unprotected." },
                { h: "Educator-guided", p: "Teachers can disable AI for any class. Daily quotas keep usage focused." },
              ].map((c) => (
                <li
                  key={c.h}
                  className="bg-white ring-1 ring-sky-100 rounded-2xl p-4 shadow-sm"
                >
                  <p className="text-sm font-extrabold text-black mb-1">{c.h}</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{c.p}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <ImpactSection />

        {/* Nevada Technology & Digital Citizenship Alignment — preserved */}
        <section
          id="nevada-alignment"
          className="bg-white py-20 sm:py-24"
          data-testid="landing-nevada-alignment"
        >
          <div className="max-w-5xl mx-auto px-4 lg:px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-800 px-3 py-1.5 rounded-full text-[11px] font-black tracking-widest uppercase mb-3">
                <Trophy className="w-3.5 h-3.5" /> Future-Ready Learning
              </div>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05] text-black"
                data-testid="landing-nevada-heading"
              >
                Nevada Technology &amp; Digital Citizenship Alignment
              </h2>
              <p className="mt-4 text-base text-gray-700 max-w-2xl mx-auto">
                Designed to support Nevada classrooms working toward
                future-ready, responsible, AI-supported learning.
              </p>
            </div>
            <div
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
              data-testid="landing-nevada-grid"
            >
              {[
                { h: "Built to support", b: "Nevada Computer Science & Integrated Technology Standards." },
                { h: "Aligned with", b: "Widely accepted digital citizenship expectations." },
                { h: "Designed with", b: "Cybersecurity awareness — students see how data is protected." },
                { h: "Supports", b: "Responsible AI-supported learning — student voice stays central." },
                { h: "Aligned with", b: "Nevada's future-ready learning emphasis — career & entrepreneurship readiness." },
                { h: "Supports", b: "CTE / Perkins V reporting through educator export tools." },
              ].map((c) => (
                <div
                  key={c.b}
                  className="bg-rose-50 ring-1 ring-rose-100 rounded-2xl p-5"
                >
                  <p className="text-[10px] font-black uppercase tracking-widest text-rose-700 mb-1">
                    {c.h}
                  </p>
                  <p className="text-sm text-gray-800 leading-relaxed">{c.b}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-gray-500 italic mt-6 max-w-2xl mx-auto">
              Alignment statements describe how the platform was designed and
              how educators may apply it. They are not formal state endorsements
              or certifications.
            </p>
            <div className="text-center mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/api/public/district-overview.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-black hover:bg-[#FFD500] hover:text-black text-white px-5 py-3 rounded-full font-bold text-sm transition-colors"
                data-testid="landing-nevada-pdf-cta"
              >
                Download District One-Pager
              </a>
              <button
                onClick={() => navigate("/security-and-student-data")}
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 px-5 py-3 rounded-full font-bold text-sm ring-2 ring-gray-200 hover:ring-[#FFD500] transition-colors"
                data-testid="landing-nevada-learn-more"
              >
                See Security &amp; Student Data{" "}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>

        <PartnerSection
          onPartner={() =>
            scrollTo("contact", "partner-cta", "Partner With Us")
          }
        />

        {/* Contact form — preserved at #contact for the pilot deep-link */}
        <section
          id="contact"
          className="bg-[#FDFBF7] py-20 sm:py-24"
          data-testid="landing-contact-section"
        >
          <div className="max-w-3xl mx-auto px-4 lg:px-6">
            <div className="text-center mb-8">
              <p className="text-xs font-black tracking-[0.22em] uppercase text-gray-500 mb-2">
                Talk to us
              </p>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-black">
                Start a conversation.
              </h2>
              <p className="mt-3 text-base text-gray-700">
                Pilots, partnerships, sponsorships, or just a hello —
                we'll respond within 2 business days.
              </p>
            </div>

            {submitted ? (
              <div
                className="bg-emerald-50 ring-1 ring-emerald-200 rounded-3xl p-8 text-center"
                data-testid="landing-contact-success"
              >
                <Mail className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                <h3 className="text-xl font-black text-black mb-1">Got it — thank you!</h3>
                <p className="text-sm text-gray-700">
                  We'll be in touch shortly at <strong>{contactForm.email}</strong>.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleContactSubmit}
                className="bg-white ring-1 ring-black/10 rounded-3xl p-6 sm:p-8 shadow-[0_12px_30px_rgba(0,0,0,0.04)] space-y-4"
                data-testid="landing-contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-600 mb-1.5" htmlFor="contact-name">
                      Name
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-[#FFD500] focus:outline-none text-sm"
                      data-testid="landing-contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-gray-600 mb-1.5" htmlFor="contact-email">
                      Email
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-[#FFD500] focus:outline-none text-sm"
                      data-testid="landing-contact-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-600 mb-1.5" htmlFor="contact-type">
                    I'm a…
                  </label>
                  <select
                    id="contact-type"
                    value={contactForm.type}
                    onChange={(e) => setContactForm({ ...contactForm, type: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-[#FFD500] focus:outline-none text-sm bg-white"
                    data-testid="landing-contact-type"
                  >
                    <option value="educator">Educator / School</option>
                    <option value="parent">Parent / Guardian</option>
                    <option value="student">Student</option>
                    <option value="sponsor">Sponsor / Partner</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-600 mb-1.5" htmlFor="contact-message">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="Tell us about your school, your students, or what you're hoping to build…"
                    className="w-full px-4 py-3 rounded-xl ring-1 ring-gray-200 focus:ring-2 focus:ring-[#FFD500] focus:outline-none text-sm resize-y"
                    data-testid="landing-contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-black hover:bg-[#FFD500] hover:text-black disabled:opacity-60 text-white px-6 py-3.5 rounded-full text-base font-bold transition-colors"
                  data-testid="landing-contact-submit"
                >
                  {submitting ? "Sending…" : (
                    <>
                      Send <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>

        <FinalCtaSection
          onPrimary={() => goRegister("student", "final-cta-start", "Start Learning (final)")}
          onDemo={handleBookDemo}
          onPartner={() => scrollTo("contact", "final-cta-partner", "Partner With Us (final)")}
        />
      </main>
    </div>
  );
}

export default LaunchLandingPage;
